import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  CalendarBlank,
  Clock,
  CheckCircle,
  Warning,
  ArrowRight,
  ArrowsClockwise,
  SunHorizon,
} from "phosphor-react";
import {
  TechVisitCard,
  TechEmptyState,
  TechLoadingState,
  syncOfflineQueue,
} from "../../components/TechComponents";
import { useTranslation } from "../../hooks/useTranslation";
import { CustomAdSlot } from "../../components/CustomAdSlot";
import { useAuth } from "../../App";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TechDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [syncing, setSyncing] = useState(false);
  
  // Track if fetch is allowed (gating logic)
  const [fetchEnabled, setFetchEnabled] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await fetch(`${API}/tech/dashboard`, {
        credentials: "include",
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      } else {
        // Only show error if we expected success (user is tech)
        if (response.status !== 401 && response.status !== 403) {
          toast.error("Failed to load dashboard");
        }
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================
  // GATED FETCH (P0 POLISH)
  // ============================================
  // Only fetch tech dashboard if:
  // 1) Auth has resolved (not loading)
  // 2) User exists and IS a technician
  // 3) Current path IS under /tech
  // This prevents spurious 401s during cross-tab auth switches
  useEffect(() => {
    // Don't enable fetch if auth is still loading
    if (authLoading) {
      return;
    }
    
    // Don't enable fetch if no user (will redirect to login)
    if (!user) {
      setLoading(false);
      return;
    }
    
    // Don't enable fetch if user is NOT a technician (wrong shell)
    if (user.role !== 'technician') {
      console.log('[TechDashboard] Skipping fetch - user is not technician');
      setLoading(false);
      return;
    }
    
    // Don't enable fetch if we're NOT on /tech path (wrong shell)
    if (!location.pathname.startsWith('/tech')) {
      console.log('[TechDashboard] Skipping fetch - not on /tech path');
      setLoading(false);
      return;
    }
    
    // All checks passed - enable fetching
    setFetchEnabled(true);
  }, [authLoading, user, location.pathname]);

  // Fetch when enabled
  useEffect(() => {
    if (fetchEnabled) {
      fetchDashboard();
    }
  }, [fetchEnabled, fetchDashboard]);

  // Sync offline queue on load (only when fetch is enabled)
  useEffect(() => {
    if (!fetchEnabled) return;
    
    const doSync = async () => {
      if (navigator.onLine) {
        setSyncing(true);
        const result = await syncOfflineQueue();
        if (result.synced > 0) {
          toast.success(`Synced ${result.synced} offline action(s)`);
          fetchDashboard();
        }
        setSyncing(false);
      }
    };
    doSync();
  }, [fetchEnabled, fetchDashboard]);

  const handleRefresh = async () => {
    setLoading(true);
    await fetchDashboard();
    toast.success("Dashboard refreshed");
  };

  const handleVisitClick = (visit) => {
    navigate(`/tech/visits/${visit.visit_id}`);
  };

  if (loading || authLoading) {
    return <TechLoadingState message="Loading your dashboard..." />;
  }

  const today = new Date();
  const stats = dashboard?.stats || {};

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold uppercase tracking-tight">
            {t("Today")}
          </h1>
          <p className="text-zinc-500">{format(today, "EEEE, MMMM d, yyyy")}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          className="border-2"
          disabled={loading || syncing}
        >
          <ArrowsClockwise size={18} className={`mr-2 ${syncing ? "animate-spin" : ""}`} />
          {t("Refresh")}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-2 border-zinc-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <CalendarBlank size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.today_total || 0}</p>
                <p className="text-xs text-zinc-500">{t("Today's Visits")}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-zinc-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle size={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.completed_today || 0}</p>
                <p className="text-xs text-zinc-500">{t("Completed")}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-zinc-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.upcoming_count || 0}</p>
                <p className="text-xs text-zinc-500">{t("Upcoming")}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-zinc-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                stats.overdue_count > 0 ? "bg-red-100" : "bg-zinc-100"
              }`}>
                <Warning size={20} className={stats.overdue_count > 0 ? "text-red-600" : "text-zinc-400"} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${stats.overdue_count > 0 ? "text-red-600" : ""}`}>
                  {stats.overdue_count || 0}
                </p>
                <p className="text-xs text-zinc-500">{t("Overdue")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overdue Visits - Show prominently if any */}
      {dashboard?.overdue?.length > 0 && (
        <Card className="border-2 border-red-200 bg-red-50">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg uppercase flex items-center gap-2 text-red-700">
              <Warning size={20} />
              {t("Overdue Visits")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {dashboard.overdue.map((visit) => (
              <TechVisitCard
                key={visit.visit_id}
                visit={visit}
                onClick={() => handleVisitClick(visit)}
                showDate
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Today's Schedule */}
      <Card className="border-2 border-zinc-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
              <SunHorizon size={20} />
              {t("Today's Schedule")}
            </CardTitle>
            {dashboard?.today?.length > 0 && (
              <Badge variant="outline" className="font-mono">
                {dashboard.today.length} {t("visit(s) found").split(" ")[0]}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {dashboard?.today?.length > 0 ? (
            dashboard.today.map((visit) => (
              <TechVisitCard
                key={visit.visit_id}
                visit={visit}
                onClick={() => handleVisitClick(visit)}
              />
            ))
          ) : (
            <TechEmptyState
              icon={CalendarBlank}
              title={t("No visits today")}
              description={t("You have no visits scheduled for today.")}
            />
          )}
        </CardContent>
      </Card>

      {/* Upcoming Visits */}
      {dashboard?.upcoming?.length > 0 && (
        <Card className="border-2 border-zinc-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <Clock size={20} />
                {t("Upcoming (Next 7 Days)")}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/tech/visits")}
                className="text-[#FF6600]"
              >
                {t("View All")}
                <ArrowRight size={16} className="ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {dashboard.upcoming.slice(0, 3).map((visit) => (
              <TechVisitCard
                key={visit.visit_id}
                visit={visit}
                onClick={() => handleVisitClick(visit)}
                showDate
              />
            ))}
            {dashboard.upcoming.length > 3 && (
              <Button
                variant="outline"
                className="w-full border-2"
                onClick={() => navigate("/tech/visits")}
              >
                {t("View All")} {dashboard.upcoming.length - 3} {t("Upcoming").toLowerCase()}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Custom Ad Slot - Tech Dashboard Sidebar */}
      <CustomAdSlot 
        placementKey="tech_dashboard_sidebar" 
        className="mt-4"
      />
    </div>
  );
}
